import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { useMemo } from 'react';
import { financePostingAccountIndexRoute } from './Finance.PostingAccount.Index';
import PostingAccount from 'models/finance/postingAccount/Model.PostingAccount';
import postingAccountDeleteApi from 'models/finance/postingAccount/delete/Api.PostingAccount.Delete';
import postingAccountSaveApi from 'models/finance/postingAccount/save/Api.PostingAccount.Save';
import Product from 'models/productManagement/product/Model.Product';

export enum PostingAccountAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyPostingAccountAction = Utils.VerifyExtends<
  Module.Finance.PostingAccount.Actions,
  Utils.ValueOf<typeof PostingAccountAction>
>;
export default function usePostingAccount() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Finance.PostingAccount.Params, Module.Finance.PostingAccount.Query>({
    route: financePostingAccountIndexRoute,
  });

  const canView =
    permissions.CREATE_LEDGERACCOUNT ||
    permissions.EDIT_LEDGERACCOUNT ||
    permissions.DELETE_LEDGERACCOUNT ||
    permissions.VIEW_LEDGERACCOUNT;

  const listApi = useApi({
    action: PostingAccount.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const productApi = useApi({ action: Product.list });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: postingAccountDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: postingAccountSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    pageRouter,
    listApi,
    productApi,
    deleteApi,
    saveApi,
    selected,
    permissions,
    views: {
      [PostingAccountAction.DELETE]:
        pageRouter.params.action === PostingAccountAction.DELETE && selected && permissions.DELETE_LEDGERACCOUNT,
      [PostingAccountAction.EDIT]:
        pageRouter.params.action === PostingAccountAction.EDIT &&
        ((permissions.CREATE_LEDGERACCOUNT && !selected) || (permissions.EDIT_LEDGERACCOUNT && selected)),
    },
  };
}

export function usePostingAccountProvider() {
  return useDataProvider<ReturnType<typeof usePostingAccount>>();
}
